import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Check,
  ChevronRight,
  Loader2,
  ShieldAlert,
  Send,
  Search,
  Zap,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Countries List ─────────────────────────────────────────────────────────
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia",
  "Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados",
  "Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina",
  "Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia",
  "Comoros","Congo","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic",
  "Denmark","Djibouti","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Estonia","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia",
  "Germany","Ghana","Greece","Guatemala","Guinea","Guyana","Haiti","Honduras",
  "Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy",
  "Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Kyrgyzstan","Laos",
  "Latvia","Lebanon","Liberia","Libya","Lithuania","Luxembourg","Madagascar",
  "Malawi","Malaysia","Maldives","Mali","Malta","Mauritania","Mauritius","Mexico",
  "Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar",
  "Namibia","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria",
  "North Macedonia","Norway","Oman","Pakistan","Palestine","Panama","Papua New Guinea",
  "Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Serbia","Sierra Leone","Singapore","Slovakia",
  "Slovenia","Somalia","South Africa","South Korea","Spain","Sri Lanka","Sudan",
  "Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania",
  "Thailand","Togo","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan",
  "Uganda","Ukraine","United Arab Emirates","United Kingdom","United States",
  "Uruguay","Uzbekistan","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

// ─── Question Definitions ──────────────────────────────────────────────────

const CAPITAL_OPTIONS = [
  { key: "A", value: "Below $100" },
  { key: "B", value: "$100 – $500" },
  { key: "C", value: "$500 – $1,000" },
  { key: "D", value: "$1,000 – $5,000" },
  { key: "E", value: "$5,000 and above" },
];

const COPYTRADE_DURATION_OPTIONS = [
  { key: "A", value: "Less than 1 month", desc: "Just started following" },
  { key: "B", value: "1 – 3 months", desc: "Getting familiar with the system" },
  { key: "C", value: "3 – 6 months", desc: "Seen multiple market cycles" },
  { key: "D", value: "More than 6 months", desc: "Long-term follower" },
];

const COPYTRADE_RESULT_OPTIONS = [
  { key: "A", value: "Profitable and satisfied" },
  { key: "B", value: "Profitable but I want more" },
  { key: "C", value: "Break even" },
  { key: "D", value: "Still learning the process" },
];

const READINESS_OPTIONS = [
  { key: "A", value: "Immediately (today)" },
  { key: "B", value: "Within 1–3 days" },
  { key: "C", value: "Within this week" },
  { key: "D", value: "Just exploring" },
];

const REFERRAL_OPTIONS = [
  { key: "A", value: "Telegram Channel" },
  { key: "B", value: "Friend / Referral" },
  { key: "C", value: "Social Media" },
  { key: "D", value: "Broker Leaderboard" },
  { key: "E", value: "Other" },
];

// ─── Animation variants ────────────────────────────────────────────────────

const slideVariants = {
  enter: (direction) => ({
    y: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    y: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    y: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

const slideTransition = {
  y: { type: "spring", stiffness: 350, damping: 35 },
  opacity: { duration: 0.25 },
};

// ─── Total steps: welcome(0) + 7 questions(1-7) + contact is split into 3 sub-fields ─
// We keep it as: 0=welcome, 1=contact, 2=capital, 3=experience, 4=signals, 5=challenge, 6=commitment, 7=readiness, 8=submit

const TOTAL_QUESTIONS = 10;

export default function SurveyForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = welcome, 1-7 = questions, 8 = submit
  const [direction, setDirection] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    full_name: "",
    telegram_username: "",
    email: "",
    country: "",
    trading_capital: "",
    trading_experience: "",
    signals_experience: "",
    referral_source: "",
    biggest_challenge: "",
    desired_capital: 1000,
    daily_target_percent: 5,
    commitment_open_ib: false,
    commitment_risk_rules: false,
    commitment_one_month: false,
    readiness_to_start: "",
    accept_ai_tools: false,
  });

  const inputRef = useRef(null);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  }, []);

  // ─── Validation per step ────────────────────────────────────────────

  const validateStep = useCallback(
    (s) => {
      switch (s) {
        case 1:
          if (!formData.full_name.trim()) return "Please enter your full name.";
          if (!formData.telegram_username.trim()) return "Please enter your Telegram username.";
          if (!formData.email.trim()) return "Please enter your email.";
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
            return "Please enter a valid email address.";
          if (!formData.country) return "Please select your country.";
          return "";
        case 2:
          return formData.trading_capital ? "" : "Please select an option.";
        case 3:
          return ""; // Calculator - always valid (has defaults)
        case 4:
          return formData.trading_experience ? "" : "Please select an option.";
        case 5:
          return formData.signals_experience ? "" : "Please select an option.";
        case 6:
          return formData.referral_source ? "" : "Please select an option.";
        case 7:
          return formData.biggest_challenge.trim() ? "" : "Please tell us why you want to join.";
        case 8:
          if (!formData.commitment_open_ib || !formData.commitment_risk_rules || !formData.commitment_one_month)
            return "You must agree to all three commitments.";
          return "";
        case 9:
          if (!formData.accept_ai_tools)
            return "You must accept the free AI Trading Tools to proceed.";
          return "";
        case 10:
          return formData.readiness_to_start ? "" : "Please select an option.";
        default:
          return "";
      }
    },
    [formData]
  );

  // ─── Navigation ─────────────────────────────────────────────────────

  const goNext = useCallback(
    (skipValidation = false) => {
      if (step === 0) {
        setDirection(1);
        setStep(1);
        setError("");
        return;
      }
      if (step >= 1 && step <= 10) {
        if (!skipValidation) {
          const err = validateStep(step);
          if (err) {
            setError(err);
            return;
          }
        }
        setError("");
        setDirection(1);
        if (step === 10) {
          setStep(11);
        } else {
          setStep(step + 1);
        }
      }
    },
    [step, validateStep]
  );

  const goBack = useCallback(() => {
    if (step > 0) {
      setDirection(-1);
      setError("");
      setStep(step - 1);
    }
  }, [step]);

  // ─── Keyboard handler ──────────────────────────────────────────────

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        // Don't trigger on textarea
        if (step === 7) return; // Let textarea handle its own enter
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, step]);

  // ─── Auto-focus inputs ──────────────────────────────────────────────

  useEffect(() => {
    // Small delay for animation
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [step]);

  // ─── Choice selection with keyboard shortcuts ───────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step < 2 || (step > 6 && step !== 10)) return; // Only for choice questions
    
    const handleChoiceKey = (e) => {
      const key = e.key.toUpperCase();
      let options;
      let field;
      
      if (step === 2) { options = CAPITAL_OPTIONS; field = "trading_capital"; }
      else if (step === 4) { options = COPYTRADE_DURATION_OPTIONS; field = "trading_experience"; }
      else if (step === 5) { options = COPYTRADE_RESULT_OPTIONS; field = "signals_experience"; }
      else if (step === 6) { options = REFERRAL_OPTIONS; field = "referral_source"; }
      else if (step === 10) { options = READINESS_OPTIONS; field = "readiness_to_start"; }
      else return;
      
      const match = options.find((o) => o.key === key);
      if (match) {
        updateField(field, match.value);
      }
    };
    
    window.addEventListener("keydown", handleChoiceKey);
    return () => window.removeEventListener("keydown", handleChoiceKey);
  }, [step, updateField]);

  // ─── Submit ────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, formData);
      toast.success("Application received. Our team will review it.");
      navigate("/success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Failed to submit. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail[0]?.msg || msg;
      }
      toast.error(msg);
      setLoading(false);
    }
  };

  // ─── Progress ──────────────────────────────────────────────────────

  const progress = step === 0 ? 0 : Math.round((Math.min(step, 10) / TOTAL_QUESTIONS) * 100);

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="typeform-bg">
      <div className="typeform-sheen" />

      {/* Powered by footer */}
      <div className="fixed bottom-3 left-0 right-0 z-40 text-center pointer-events-none">
        <span className="text-[0.55rem] font-mono uppercase tracking-[0.15em] text-[hsl(240,3%,52%)]">
          Powered by UNDERADS
        </span>
      </div>

      {/* Progress bar + step counter */}
      {step > 0 && step <= 10 && (
        <>
          <div
            className="typeform-progress-bar"
            style={{ width: `${progress}%` }}
            data-testid="form-progress"
          />
          <div className="fixed top-3 right-4 sm:right-6 z-50">
            <span className="text-[0.65rem] font-mono text-[hsl(240,3%,40%)]">
              {Math.min(step, 10)} / 10
            </span>
          </div>
        </>
      )}

      {/* INTRO PAGE - Full screen, separate from slide container */}
      {step === 0 && (
        <AnimatePresence mode="wait">
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <WelcomeSlide onStart={goNext} />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Navigation arrows */}
      {step > 0 && step <= 11 && (
        <div className="typeform-nav">
          <button
            className="typeform-nav-btn"
            onClick={goBack}
            disabled={step === 0}
            data-testid="nav-up-button"
            aria-label="Previous question"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            className="typeform-nav-btn"
            onClick={goNext}
            disabled={step >= 11}
            data-testid="nav-down-button"
            aria-label="Next question"
          >
            <ArrowDown className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Question Slides (step 1-8) */}
      {step > 0 && (
        <div className="typeform-slide-container">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              className="typeform-slide"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={slideTransition}
            >
              {/* ─── Step 1: Contact Info ────────────────── */}
              {step === 1 && (
              <ContactSlide
                formData={formData}
                updateField={updateField}
                error={error}
                onNext={goNext}
                inputRef={inputRef}
              />
            )}

            {/* ─── Step 2: Trading Capital ─────────────── */}
            {step === 2 && (
              <ChoiceSlide
                number={2}
                question="What is your current trading capital?"
                options={CAPITAL_OPTIONS}
                value={formData.trading_capital}
                onChange={(val) => updateField("trading_capital", val)}
                error={error}
                onNext={goNext}
                testId="trading-capital"
              />
            )}

            {/* ─── Step 3: Investment Calculator ──────── */}
            {step === 3 && (
              <CalculatorSlide
                formData={formData}
                updateField={updateField}
                onNext={goNext}
              />
            )}

            {/* ─── Step 4: Copytrade Duration ──────────── */}
            {step === 4 && (
              <ChoiceSlide
                number={4}
                question="How long have you been following our copytrade?"
                options={COPYTRADE_DURATION_OPTIONS}
                value={formData.trading_experience}
                onChange={(val) => updateField("trading_experience", val)}
                error={error}
                onNext={goNext}
                testId="trading-experience"
              />
            )}

            {/* ─── Step 5: Copytrade Results ──────────── */}
            {step === 5 && (
              <ChoiceSlide
                number={5}
                question="How has your copytrade experience been so far?"
                options={COPYTRADE_RESULT_OPTIONS}
                value={formData.signals_experience}
                onChange={(val) => updateField("signals_experience", val)}
                error={error}
                onNext={goNext}
                testId="signals-experience"
              />
            )}

            {/* ─── Step 6: How Did You Find Us ──────────── */}
            {step === 6 && (
              <ChoiceSlide
                number={6}
                question="How did you first find out about us?"
                options={REFERRAL_OPTIONS}
                value={formData.referral_source}
                onChange={(val) => updateField("referral_source", val)}
                error={error}
                onNext={goNext}
                testId="referral-source"
              />
            )}

            {/* ─── Step 7: Why You Deserve This ──────────── */}
            {step === 7 && (
              <TextAreaSlide
                number={7}
                question="Why should we keep going for you?"
                hint="We almost stopped. Convince us you're worth one more round."
                value={formData.biggest_challenge}
                onChange={(val) => updateField("biggest_challenge", val)}
                error={error}
                onNext={goNext}
                inputRef={inputRef}
              />
            )}

            {/* ─── Step 8: Commitment ─────────────────── */}
            {step === 8 && (
              <CommitmentSlide
                formData={formData}
                updateField={updateField}
                error={error}
                onNext={goNext}
              />
            )}

            {/* ─── Step 9: AI Trading Tools (Free Gift) ──── */}
            {step === 9 && (
              <AIToolsSlide
                formData={formData}
                updateField={updateField}
                error={error}
                onNext={goNext}
              />
            )}

            {/* ─── Step 10: Readiness ─────────────────── */}
            {step === 10 && (
              <ChoiceSlide
                number={10}
                question="If you are accepted, when can you start?"
                options={READINESS_OPTIONS}
                value={formData.readiness_to_start}
                onChange={(val) => updateField("readiness_to_start", val)}
                error={error}
                onNext={goNext}
                testId="readiness"
              />
            )}

            {/* ─── Step 11: Submit ──────────────────────── */}
            {step === 11 && (
              <SubmitSlide
                loading={loading}
                onSubmit={handleSubmit}
                onBack={goBack}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function WelcomeSlide({ onStart }) {
  const navigate = useNavigate();
  const [tapCount, setTapCount] = useState(0);

  const handleBadgeTap = () => {
    const next = tapCount + 1;
    setTapCount(next);
    if (next >= 7) {
      navigate("/admin");
    }
  };

  return (
    <div data-testid="welcome-slide" className="intro-container">
      {/* Animated grid bg */}
      <div className="intro-grid" />
      
      {/* Scan line */}
      <div className="intro-scanline" />
      
      {/* Horizontal accent lines */}
      <div className="intro-hline intro-hline-1" />
      <div className="intro-hline intro-hline-2" />
      
      {/* Vertical accent lines */}
      <div className="intro-vline intro-vline-1" />
      <div className="intro-vline intro-vline-2" />
      
      {/* Corner brackets */}
      <div className="intro-corner intro-corner-tl" />
      <div className="intro-corner intro-corner-tr" />
      <div className="intro-corner intro-corner-bl" />
      <div className="intro-corner intro-corner-br" />
      
      {/* Glow orb */}
      <div className="intro-glow-orb" />
      
      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-3xl mx-auto">
        {/* Top data stream */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overflow-hidden mb-5 sm:mb-8"
        >
          <div className="data-stream">
            STRATEGY BANNED /// TWICE /// BUT WE'RE STILL HERE /// FOR THE REAL ONES /// STRATEGY BANNED /// TWICE ///
          </div>
        </motion.div>

        {/* Status badge - secret admin access (7 taps) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-[hsl(350,58%,42%,0.2)] bg-[hsl(350,58%,42%,0.05)] mb-5 sm:mb-8 cursor-default select-none"
          onClick={handleBadgeTap}
        >
          <span className="status-dot" />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[hsl(350,58%,48%)]">
            One Last Time
          </span>
        </motion.div>

        {/* Main title */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="mb-3 sm:mb-4"
        >
          <h1
            className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-normal tracking-[-0.03em] text-white leading-[0.95] elegant-title"
            data-testid="form-title"
          >
            THE LAST DANCE
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.0, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="mb-5 sm:mb-6"
        >
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-normal tracking-[-0.02em] text-[hsl(350,58%,48%)] leading-[0.95]">
            CAPITAL
          </h2>
        </motion.div>

        {/* Red line separator */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.3, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
          className="red-line w-32 sm:w-48 mx-auto mb-6 sm:mb-8"
        />

        {/* Tagline - merajuk tapi power */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.6 }}
          className="text-sm sm:text-base md:text-lg text-[hsl(30,10%,88%)] max-w-xs sm:max-w-lg mx-auto leading-relaxed mb-3 sm:mb-4 px-2 font-medium italic"
        >
          "When they pulled the plug, you stayed. That meant something."
        </motion.p>

        {/* Sub copy - belagak + emotional */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.6 }}
          className="text-xs sm:text-sm text-[hsl(240,3%,48%)] max-w-xs sm:max-w-md mx-auto leading-relaxed mb-8 sm:mb-10 px-2"
        >
          We didn't beg. We didn't explain. We went silent and built something they can't touch.
          Most people disappeared after the ban. But you're still here.
          This isn't for everyone who followed. It's for the ones who never left.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1, duration: 0.6 }}
        >
          <button
            className="typeform-start-btn pulse-ring"
            onClick={onStart}
            data-testid="start-button"
          >
            I Held The Line
            <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>

        {/* Bottom meta */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 0.8 }}
          className="mt-6 sm:mt-8 flex items-center justify-center gap-2 sm:gap-6 flex-wrap px-2"
        >
          <span className="text-[0.55rem] sm:text-[0.65rem] font-mono uppercase tracking-[0.08em] sm:tracking-[0.15em] text-[hsl(240,3%,30%)]">
            10 Questions
          </span>
          <span className="w-1 h-1 rounded-full bg-[hsl(240,4%,22%)]" />
          <span className="text-[0.55rem] sm:text-[0.65rem] font-mono uppercase tracking-[0.08em] sm:tracking-[0.15em] text-[hsl(240,3%,30%)]">
            Reviewed Personally
          </span>
          <span className="w-1 h-1 rounded-full bg-[hsl(240,4%,22%)]" />
          <span className="text-[0.55rem] sm:text-[0.65rem] font-mono uppercase tracking-[0.08em] sm:tracking-[0.15em] text-[hsl(350,58%,42%,0.5)]">
            Final Selection
          </span>
        </motion.div>

        {/* Bottom data stream */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.8, duration: 0.8 }}
          className="overflow-hidden mt-6 sm:mt-10"
        >
          <div className="data-stream" style={{ animationDirection: "reverse" }}>
            BANNED TWICE /// STILL STANDING /// THE LAST DANCE CAPITAL /// LOYALTY OVER NUMBERS /// NO MORE DEAD WEIGHT ///
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ContactSlide({ formData, updateField, error, onNext, inputRef }) {
  const [countrySearch, setCountrySearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const filteredCountries = COUNTRIES.filter((c) =>
    c.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectCountry = (country) => {
    updateField("country", country);
    setCountrySearch(country);
    setShowDropdown(false);
  };

  return (
    <div data-testid="contact-slide">
      <div className="typeform-question-number">
        1 <span className="arrow"><ArrowRight className="h-3 w-3 inline" /></span>
      </div>
      <h2 className="typeform-question">Full Name & Contact</h2>
      <p className="typeform-hint">We'll use this to reach you if your application is accepted.</p>

      <div className="space-y-5">
        <div>
          <label className="text-xs uppercase tracking-wider text-[hsl(240,3%,50%)] font-medium mb-2 block">
            Full Name *
          </label>
          <input
            ref={inputRef}
            type="text"
            className="typeform-input"
            placeholder="Type your full name here..."
            value={formData.full_name}
            onChange={(e) => updateField("full_name", e.target.value)}
            data-testid="full-name-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[hsl(240,3%,50%)] font-medium mb-2 block">
            Telegram Username *
          </label>
          <input
            type="text"
            className="typeform-input"
            placeholder="@yourusername"
            value={formData.telegram_username}
            onChange={(e) => updateField("telegram_username", e.target.value)}
            data-testid="telegram-username-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[hsl(240,3%,50%)] font-medium mb-2 block">
            Email *
          </label>
          <input
            type="email"
            className="typeform-input"
            placeholder="you@example.com"
            value={formData.email}
            onChange={(e) => updateField("email", e.target.value)}
            data-testid="email-input"
          />
        </div>
        <div ref={dropdownRef} className="relative">
          <label className="text-xs uppercase tracking-wider text-[hsl(240,3%,50%)] font-medium mb-2 block">
            Country *
          </label>
          <div className="relative">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(240,3%,35%)]" />
            <input
              type="text"
              className="typeform-input"
              style={{ paddingLeft: "24px" }}
              placeholder="Search country..."
              value={countrySearch}
              onChange={(e) => {
                setCountrySearch(e.target.value);
                setShowDropdown(true);
                if (!e.target.value) updateField("country", "");
              }}
              onFocus={() => setShowDropdown(true)}
              data-testid="country-search-input"
            />
          </div>
          {showDropdown && countrySearch.length > 0 && (
            <div
              className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border border-[hsl(240,4%,20%)] bg-[hsl(240,5%,9%)] shadow-lg"
              data-testid="country-dropdown"
            >
              {filteredCountries.length === 0 ? (
                <div className="px-4 py-3 text-sm text-[hsl(240,3%,40%)]">No country found</div>
              ) : (
                filteredCountries.slice(0, 8).map((country) => (
                  <button
                    key={country}
                    className="w-full text-left px-4 py-2.5 text-sm text-[hsl(30,8%,85%)] hover:bg-[hsl(350,58%,42%,0.08)] hover:text-white transition-colors"
                    onClick={() => selectCountry(country)}
                    data-testid={`country-option-${country}`}
                  >
                    {country}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {error && <p className="typeform-error" data-testid="form-error-text">{error}</p>}

      <div className="mt-8 flex items-center gap-3">
        <button className="typeform-ok-btn" onClick={() => onNext()} data-testid="ok-button">
          OK <Check className="h-4 w-4" />
        </button>
        <span className="typeform-enter-hint">
          press <strong>Enter ↵</strong>
        </span>
      </div>
    </div>
  );
}

function CalculatorSlide({ formData, updateField, onNext }) {
  const capital = formData.desired_capital;
  const pct = formData.daily_target_percent;
  const dailyProfit = capital * (pct / 100);
  const weeklyProfit = dailyProfit * 5;
  const monthlyProfit = dailyProfit * 22;

  const formatMoney = (val) => {
    if (val >= 1000) return "$" + val.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return "$" + val.toFixed(2);
  };

  return (
    <div data-testid="calculator-slide">
      <div className="typeform-question-number">
        3 <span className="arrow"><ArrowRight className="h-3 w-3 inline" /></span>
      </div>
      <h2 className="typeform-question">How much are you willing to commit to the new strategy?</h2>
      <p className="typeform-hint">
        This is separate from your current capital. How much are you ready to put on the line for this?
      </p>

      <div className="space-y-8">
        {/* Capital Slider */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="text-xs uppercase tracking-wider text-[hsl(240,3%,50%)] font-medium">
              Capital to Commit
            </label>
            <span className="text-xl sm:text-2xl font-semibold text-white font-mono" data-testid="capital-display">
              ${capital.toLocaleString()}
            </span>
          </div>
          <Slider
            value={[capital]}
            onValueChange={(val) => updateField("desired_capital", val[0])}
            min={100}
            max={50000}
            step={100}
            data-testid="capital-slider"
          />
          <div className="flex justify-between mt-2">
            <span className="text-[0.65rem] text-[hsl(240,3%,40%)] font-mono">$100</span>
            <span className="text-[0.65rem] text-[hsl(240,3%,40%)] font-mono">$50,000</span>
          </div>
        </div>

        {/* Daily % Slider */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="text-xs uppercase tracking-wider text-[hsl(240,3%,50%)] font-medium">
              Daily Target
            </label>
            <span className="text-xl sm:text-2xl font-semibold text-[hsl(350,58%,48%)] font-mono" data-testid="percent-display">
              {pct}%
            </span>
          </div>
          <Slider
            value={[pct]}
            onValueChange={(val) => updateField("daily_target_percent", val[0])}
            min={1}
            max={20}
            step={0.5}
            data-testid="percent-slider"
          />
          <div className="flex justify-between mt-2">
            <span className="text-[0.65rem] text-[hsl(240,3%,40%)] font-mono">1%</span>
            <span className="text-[0.65rem] text-[hsl(240,3%,40%)] font-mono">20%</span>
          </div>
        </div>

        {/* Results */}
        <div className="rounded-xl border border-[hsl(240,4%,18%)] bg-[hsl(240,5%,9%)] p-4 sm:p-5 space-y-3">
          <p className="text-xs uppercase tracking-wider text-[hsl(240,3%,45%)] font-semibold mb-3">
            Projected Earnings
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[hsl(240,3%,55%)]">Daily</span>
            <span className="text-lg font-semibold text-white font-mono">{formatMoney(dailyProfit)}</span>
          </div>
          <div className="h-px bg-[hsl(240,4%,16%)]" />
          <div className="flex items-center justify-between">
            <span className="text-sm text-[hsl(240,3%,55%)]">Weekly <span className="text-[0.65rem] text-[hsl(240,3%,35%)]">(5 days)</span></span>
            <span className="text-lg font-semibold text-white font-mono">{formatMoney(weeklyProfit)}</span>
          </div>
          <div className="h-px bg-[hsl(240,4%,16%)]" />
          <div className="flex items-center justify-between">
            <span className="text-sm text-[hsl(240,3%,55%)]">Monthly <span className="text-[0.65rem] text-[hsl(240,3%,35%)]">(22 days)</span></span>
            <span className="text-xl font-bold text-[hsl(350,58%,48%)] font-mono">{formatMoney(monthlyProfit)}</span>
          </div>
        </div>
      </div>

      <div className="mt-8 pb-4 flex items-center gap-3">
        <button className="typeform-ok-btn" onClick={() => onNext()} data-testid="ok-button">
          OK <Check className="h-4 w-4" />
        </button>
        <span className="typeform-enter-hint">
          press <strong>Enter ↵</strong>
        </span>
      </div>
    </div>
  );
}


function ChoiceSlide({ number, question, options, value, onChange, error, onNext, testId }) {
  const [justSelected, setJustSelected] = useState(false);
  
  const handleSelect = (val) => {
    onChange(val);
    setJustSelected(true);
  };

  // Auto-advance when a choice is made
  useEffect(() => {
    if (justSelected && value) {
      const timer = setTimeout(() => {
        onNext(true);
        setJustSelected(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [justSelected, value, onNext]);

  return (
    <div data-testid={`${testId}-slide`}>
      <div className="typeform-question-number">
        {number} <span className="arrow"><ArrowRight className="h-3 w-3 inline" /></span>
      </div>
      <h2 className="typeform-question">{question}</h2>
      <p className="typeform-hint">Choose one option</p>

      <div className="space-y-3" data-testid={`${testId}-radio`}>
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`typeform-choice ${value === opt.value ? "selected" : ""}`}
            onClick={() => handleSelect(opt.value)}
            data-testid={`${testId}-option-${opt.key}`}
          >
            <span className="typeform-choice-key">{opt.key}</span>
            <div>
              <span className="typeform-choice-label">{opt.value}</span>
              {opt.desc && <p className="typeform-choice-desc">{opt.desc}</p>}
            </div>
            {value === opt.value && (
              <Check className="h-4 w-4 text-[hsl(350,58%,48%)] ml-auto flex-shrink-0" />
            )}
          </button>
        ))}
      </div>

      {error && <p className="typeform-error" data-testid="form-error-text">{error}</p>}
    </div>
  );
}

function TextAreaSlide({ number, question, hint, value, onChange, error, onNext, inputRef }) {
  return (
    <div data-testid="biggest-challenge-slide">
      <div className="typeform-question-number">
        {number} <span className="arrow"><ArrowRight className="h-3 w-3 inline" /></span>
      </div>
      <h2 className="typeform-question">{question}</h2>
      <p className="typeform-hint">{hint}</p>

      <textarea
        ref={inputRef}
        className="typeform-textarea"
        placeholder="Type your answer here..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid="biggest-challenge-textarea"
      />

      {error && <p className="typeform-error" data-testid="form-error-text">{error}</p>}

      <div className="mt-6 flex items-center gap-3">
        <button className="typeform-ok-btn" onClick={() => onNext()} data-testid="ok-button">
          OK <Check className="h-4 w-4" />
        </button>
        <span className="typeform-enter-hint">
          press <strong>Shift + Enter</strong> for new line
        </span>
      </div>
    </div>
  );
}

function AIToolsSlide({ formData, updateField, error, onNext }) {
  const tools = [
    { icon: "chart", title: "AI Market Scanner", desc: "Real-time market analysis powered by AI. Spots high-probability setups before the crowd." },
    { icon: "brain", title: "Smart Entry/Exit Signals", desc: "AI-calculated entry and exit points. No more guessing, no more emotions." },
    { icon: "brain", title: "AI Fundamental Analysis", desc: "AI-powered fundamental analysis on currencies, commodities, and indices. Know the 'why' behind every move." },
  ];

  return (
    <div data-testid="ai-tools-slide">
      <div className="typeform-question-number">
        9 <span className="arrow"><ArrowRight className="h-3 w-3 inline" /></span>
      </div>
      <h2 className="typeform-question">
        Free Gift: AI Trading Tools
      </h2>
      <p className="typeform-hint">
        Every member gets these tools for free. This is mandatory and part of the package. No extra cost.
      </p>

      <div className="space-y-3 mb-6">
        {tools.map((tool) => (
          <div
            key={tool.title}
            className="flex items-start gap-4 p-4 rounded-xl border border-[hsl(240,4%,18%)] bg-[hsl(240,5%,9%)]"
          >
            <div className="w-10 h-10 rounded-lg bg-[hsl(350,58%,42%,0.1)] flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5 text-[hsl(350,58%,48%)]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[hsl(30,8%,90%)]">{tool.title}</p>
              <p className="text-xs text-[hsl(240,3%,48%)] mt-0.5">{tool.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Mandatory acceptance */}
      <button
        className={`typeform-checkbox w-full ${formData.accept_ai_tools ? "checked" : ""}`}
        onClick={() => updateField("accept_ai_tools", !formData.accept_ai_tools)}
        data-testid="accept-ai-tools-checkbox"
      >
        <span className="typeform-checkbox-box">
          {formData.accept_ai_tools && <Check className="h-3.5 w-3.5 text-white" />}
        </span>
        <span className="typeform-checkbox-label">
          Yes, I want the free AI Trading Tools as part of my membership
        </span>
      </button>

      {error && <p className="typeform-error mt-3" data-testid="form-error-text">{error}</p>}

      <div className="mt-6 flex items-center gap-3">
        <button
          className="typeform-ok-btn"
          onClick={() => onNext()}
          disabled={!formData.accept_ai_tools}
          data-testid="ok-button"
        >
          OK <Check className="h-4 w-4" />
        </button>
        {formData.accept_ai_tools && (
          <span className="typeform-enter-hint">
            press <strong>Enter ↵</strong>
          </span>
        )}
      </div>
    </div>
  );
}


function CommitmentSlide({ formData, updateField, error, onNext }) {
  const commitments = [
    { field: "commitment_open_ib", label: "Open a fresh account under our new system (IB)" },
    { field: "commitment_risk_rules", label: "Follow the strategy rules without question" },
    { field: "commitment_one_month", label: "Commit for minimum 1 month. No excuses." },
  ];

  const allChecked = formData.commitment_open_ib && formData.commitment_risk_rules && formData.commitment_one_month;

  return (
    <div data-testid="commitment-slide">
      <div className="typeform-question-number">
        8 <span className="arrow"><ArrowRight className="h-3 w-3 inline" /></span>
      </div>
      <h2 className="typeform-question">
        This is not a free ride. Are you in or out?
      </h2>
      <p className="typeform-hint">
        Tick <strong className="text-[hsl(350,58%,48%)]">all three</strong>. If you can't commit to these, don't waste our time.
      </p>

      <div className="space-y-3">
        {commitments.map(({ field, label }) => (
          <button
            key={field}
            className={`typeform-checkbox ${formData[field] ? "checked" : ""}`}
            onClick={() => updateField(field, !formData[field])}
            data-testid={`${field.replace(/_/g, "-")}-checkbox`}
          >
            <span className="typeform-checkbox-box">
              {formData[field] && <Check className="h-3.5 w-3.5 text-white" />}
            </span>
            <span className="typeform-checkbox-label">{label}</span>
          </button>
        ))}
      </div>

      {error && <p className="typeform-error" data-testid="form-error-text">{error}</p>}

      <div className="mt-8 flex items-center gap-3">
        <button
          className="typeform-ok-btn"
          onClick={() => onNext()}
          disabled={!allChecked}
          data-testid="ok-button"
        >
          OK <Check className="h-4 w-4" />
        </button>
        {allChecked && (
          <span className="typeform-enter-hint">
            press <strong>Enter ↵</strong>
          </span>
        )}
      </div>
    </div>
  );
}

function SubmitSlide({ loading, onSubmit, onBack }) {
  return (
    <div className="text-center" data-testid="submit-slide">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(350,58%,42%,0.12)] mb-6">
        <Send className="h-7 w-7 text-[hsl(350,58%,48%)]" />
      </div>
      <h2 className="typeform-question" style={{ textAlign: "center" }}>
        Last chance. You sure about this?
      </h2>
      <p className="typeform-hint" style={{ textAlign: "center", maxWidth: "420px", margin: "8px auto 36px" }}>
        Once you submit, we'll review it personally. Not everyone will hear back. That's the point.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        <button
          className="rounded-lg px-5 h-11 text-sm font-medium border border-[hsl(240,4%,22%)] bg-[hsl(240,5%,12%)] text-[hsl(30,8%,80%)] hover:bg-[hsl(240,5%,16%)] hover:text-white transition-colors"
          onClick={onBack}
        >
          Review Answers
        </button>
        <button
          className="typeform-start-btn"
          onClick={onSubmit}
          disabled={loading}
          data-testid="submit-application-button"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Submit Application
              <ChevronRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
